// View as, in the shell: the banner that says whose dashboard this is while staff look, the picker that chooses them,
// and the one line a page shows where its actions would be. The data and the rules live in viewAsModel.js and in
// AppContext (startViewAs / stopViewAs); the server (frontend/server/viewAs.ts) decides who may look at whom.
import { useEffect, useId, useState } from "react";
import { Eye, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { viewAsLabel, viewAsRoleLabel } from "./viewAsModel";

const STATUS_LABEL = { active: "Active", pending: "Pending sign-in" };

export function ViewAsBanner({ viewingAs, onStop }) {
  if (!viewingAs) return null;
  return (
    <div
      data-testid="view-as-banner"
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-[#F59E0B] px-4 py-2 text-sm text-[#1F1300] md:px-8"
    >
      <p className="m-0 flex min-w-0 items-start gap-2 font-semibold">
        <Eye size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
        {/* The whole line is read on a phone too: a long email wraps rather than hiding the role and "read only". */}
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">{viewAsLabel(viewingAs)}</span>
      </p>
      <button
        type="button"
        data-testid="view-as-stop"
        onClick={onStop}
        className="shrink-0 rounded-full bg-[#0F2442] px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#003B8D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2442] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F59E0B]"
      >
        Stop viewing as
      </button>
    </div>
  );
}

/** The sentence a page shows where its actions were. Nothing when the person can act. */
export function ReadOnlyNotice({ className = "" }) {
  const { readOnly, readOnlyMessage } = useApp();
  if (!readOnly) return null;
  return (
    <p
      data-testid="read-only-notice"
      role="status"
      className={`rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-sm font-semibold text-[#7C3A00] ${className}`.trim()}
    >
      {readOnlyMessage}
    </p>
  );
}

/** The picker as drawn: the brand's people, one button each. The dev fixture draws it from made-up rows. */
export function ViewAsPickerDialog({ open, onOpenChange, clientName, members = [], status = "ready", error = null, busyEmail = null, chooseError = null, onChoose }) {
  const [query, setQuery] = useState("");
  const countId = useId();
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  const normalized = query.trim().toLowerCase();
  const shown = members.filter((member) => !normalized || member.email.includes(normalized));
  const searchable = members.length > 6;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="view-as-picker" className="max-w-md gap-3 border-sm-ink/10 bg-white text-sm-ink">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-lg font-bold text-sm-ink">View as a member of {clientName}</DialogTitle>
          <DialogDescription className="text-sm text-sm-ink/70">
            Pick a person to see the HUB exactly as they do. Everything stays read only while you look.
          </DialogDescription>
        </DialogHeader>

        {status === "loading" ? (
          <p role="status" className="flex items-center gap-2 py-4 text-sm text-sm-muted">
            <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Reading who is in this workspace…
          </p>
        ) : null}
        {status === "error" ? (
          <p role="alert" className="rounded-lg border border-[#EF4343]/30 bg-[#EF4343]/[0.08] px-3 py-2 text-sm text-[#B91C1C]">
            {error?.message || "The people in this workspace could not be read."}
          </p>
        ) : null}

        {status === "ready" ? (
          <>
            {searchable ? (
              <div className="relative">
                <Search size={13} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm-muted" />
                <input
                  data-testid="view-as-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label="Search people"
                  aria-describedby={countId}
                  placeholder="Search by email"
                  autoComplete="off"
                  className="h-9 w-full rounded-md border border-sm-ink/15 bg-sm-surface pl-8 pr-2 text-sm text-sm-ink outline-none placeholder:text-sm-muted focus:border-sm-primary/50 focus:ring-2 focus:ring-sm-primary/15"
                />
              </div>
            ) : null}
            <p id={countId} role="status" aria-live="polite" className="m-0 text-xs text-sm-muted">
              {normalized ? `${shown.length} of ${members.length} people` : `${members.length} ${members.length === 1 ? "person" : "people"}`}
            </p>
            {shown.length === 0 ? (
              <p data-testid="view-as-empty" className="rounded-lg border border-dashed border-sm-ink/15 px-3 py-5 text-center text-sm text-sm-muted">
                {members.length === 0 ? "No one else is in this workspace yet." : "No one matches that."}
              </p>
            ) : (
              <ul aria-label="People in this workspace" className="m-0 max-h-[50vh] list-none divide-y divide-sm-ink/10 overflow-y-auto rounded-lg border border-sm-ink/10 p-0">
                {shown.map((member) => (
                  <li key={member.id ?? member.email}>
                    <button
                      type="button"
                      data-testid={`view-as-option-${member.email}`}
                      disabled={Boolean(busyEmail)}
                      aria-busy={busyEmail === member.email || undefined}
                      onClick={() => onChoose(member)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-sm-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sm-primary disabled:cursor-progress disabled:opacity-60"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-sm-ink">{member.email}</span>
                        <span className="block text-xs text-sm-muted">
                          {viewAsRoleLabel(member.role)}
                          {member.status && member.status !== "active" ? ` · ${STATUS_LABEL[member.status] || member.status}` : ""}
                        </span>
                      </span>
                      {busyEmail === member.email
                        ? <Loader2 size={14} className="shrink-0 animate-spin text-sm-primary" aria-hidden="true" />
                        : <span aria-hidden="true" className="shrink-0 text-xs font-bold text-sm-primary">View</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}

        {chooseError ? (
          <p role="alert" data-testid="view-as-choose-error" className="rounded-lg border border-[#EF4343]/30 bg-[#EF4343]/[0.08] px-3 py-2 text-sm text-[#B91C1C]">
            {chooseError?.message || "That person could not be viewed."}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** The picker on a live dashboard: reads the brand's people when opened, starts the look on a choice. */
export function ViewAsPicker({ open, onOpenChange }) {
  const { api, activeClient, startViewAs } = useApp();
  const [people, setPeople] = useState({ status: "idle", members: [], error: null });
  const [busyEmail, setBusyEmail] = useState(null);
  const [chooseError, setChooseError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setChooseError(null);
    setPeople({ status: "loading", members: [], error: null });
    Promise.resolve()
      .then(() => api.getViewAsMembers())
      .then((payload) => {
        if (!cancelled) setPeople({ status: "ready", members: Array.isArray(payload?.members) ? payload.members : [], error: null });
      })
      .catch((error) => {
        if (!cancelled) setPeople({ status: "error", members: [], error });
      });
    return () => { cancelled = true; };
  }, [api, open]);

  const choose = async (member) => {
    setBusyEmail(member.email);
    setChooseError(null);
    try {
      // The page stays where it is and re-reads as the person: All brands as them, the Team page as them.
      await startViewAs(member.email);
      onOpenChange(false);
    } catch (error) {
      setChooseError(error);
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <ViewAsPickerDialog
      open={open}
      onOpenChange={onOpenChange}
      clientName={activeClient?.clientName ?? "this workspace"}
      members={people.members}
      status={people.status === "idle" ? "loading" : people.status}
      error={people.error}
      busyEmail={busyEmail}
      chooseError={chooseError}
      onChoose={choose}
    />
  );
}
